import React from 'react';
import {
  Box, Typography, Paper, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Card, CardContent,
  Divider, ToggleButton, ToggleButtonGroup, Stack, CircularProgress,
  useTheme, alpha,
} from '@mui/material';
import { CheckCircleOutline, ArrowUpward } from '@mui/icons-material';
import {
  BusinessPlanFromAPI, PlanQuotas,
  TOP_TIER_KEY, PLAN_QUOTAS, QUOTA_DISPLAY, formatPrice,
} from './types';

interface UpgradeSectionProps {
  currentPlanKey: string;
  currentPlanDef: BusinessPlanFromAPI | null;
  currentQuotas: PlanQuotas;
  companyBillingCycle: 'monthly' | 'annual';
  billingCycle: 'monthly' | 'annual';
  setBillingCycle: (v: 'monthly' | 'annual') => void;
  upgradePlans: BusinessPlanFromAPI[];
  selectedUpgradePlan: string | null;
  setSelectedUpgradePlan: (v: string | null) => void;
  selectedUpgradeDef: BusinessPlanFromAPI | null;
  currencySymbol: string;
  upgradeLoading: boolean;
  getPrice: (plan: BusinessPlanFromAPI, cycle: 'monthly' | 'annual') => number;
  onUpgrade: () => void;
}

export const UpgradeSection: React.FC<UpgradeSectionProps> = ({
  currentPlanKey: _currentPlanKey, currentPlanDef, currentQuotas, companyBillingCycle,
  billingCycle, setBillingCycle, upgradePlans, selectedUpgradePlan,
  setSelectedUpgradePlan, selectedUpgradeDef,
  currencySymbol, upgradeLoading, getPrice, onUpgrade,
}) => {
  const theme = useTheme();

  const currentPrice = currentPlanDef ? getPrice(currentPlanDef, companyBillingCycle) : 0;
  const targetPrice = selectedUpgradeDef ? getPrice(selectedUpgradeDef, billingCycle) : 0;

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upgrade to a higher-tier plan. New pricing takes effect from your next billing cycle.
        {billingCycle === 'annual'
          ? ' Annual plans are billed monthly at a discounted rate.'
          : ''}
      </Typography>

      <ToggleButtonGroup
        value={billingCycle} exclusive
        onChange={(_e, val) => { if (val) { setBillingCycle(val); setSelectedUpgradePlan(null); } }}
        size="small" sx={{ mb: 3 }}
      >
        <ToggleButton value="monthly">Monthly</ToggleButton>
        <ToggleButton value="annual">Annual (Save ~20%)</ToggleButton>
      </ToggleButtonGroup>

      {upgradePlans.length > 0 ? (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          {upgradePlans.map(plan => {
            const isSelected = selectedUpgradePlan === plan.key;
            const price = getPrice(plan, billingCycle);
            return (
              <Card
                key={plan.key} variant="outlined"
                sx={{
                  flex: 1, cursor: 'pointer',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  borderWidth: isSelected ? 2 : 1,
                  bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.light', boxShadow: 2 },
                  ...(isSelected && {
                    animation: 'cardGlow 2s ease-in-out infinite',
                    '@keyframes cardGlow': {
                      '0%, 100%': {
                        boxShadow: `0 0 4px ${alpha(theme.palette.secondary.main, 0.4)}`,
                        borderColor: theme.palette.secondary.main,
                      },
                      '50%': {
                        boxShadow: `0 0 16px ${alpha(theme.palette.secondary.main, 0.7)}, 0 0 30px ${alpha(theme.palette.secondary.main, 0.3)}`,
                        borderColor: theme.palette.secondary.light,
                      },
                    },
                  }),
                }}
                onClick={() => setSelectedUpgradePlan(plan.key)}
              >
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={700}>{plan.label}</Typography>
                    {plan.key === TOP_TIER_KEY && <Chip label="Best Value" color="warning" size="small" />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">{plan.tagline}</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>
                    {formatPrice(price, currencySymbol)}
                    <Typography component="span" variant="body2" color="text.secondary">/month</Typography>
                  </Typography>
                  {billingCycle === 'annual' && (
                    <Typography variant="caption" color="success.main">Billed monthly on annual commitment</Typography>
                  )}
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {plan.noOfUsers} user{plan.noOfUsers > 1 ? 's' : ''}
                  </Typography>
                  {plan.highlights.map((h, idx) => (
                    <Box key={idx}>
                      {h.features.map((f, fi) => (
                        <Typography key={fi} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckCircleOutline sx={{ fontSize: 14, color: 'success.main' }} /> {f}
                        </Typography>
                      ))}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
          No {billingCycle} upgrade options available. Try switching to {billingCycle === 'monthly' ? 'Annual' : 'Monthly'}.
        </Typography>
      )}

      {selectedUpgradeDef && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Plan Comparison</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                  <TableCell sx={{ fontWeight: 700 }}>Feature</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {currentPlanDef?.label || 'Current'} ({companyBillingCycle})
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {selectedUpgradeDef.label} ({billingCycle})
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Price / Month</TableCell>
                  <TableCell align="center">{formatPrice(currentPrice, currencySymbol)}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>{formatPrice(targetPrice, currencySymbol)}</TableCell>
                  <TableCell align="center">
                    {targetPrice > currentPrice
                      ? <Chip label={`+${formatPrice(targetPrice - currentPrice, currencySymbol)}`} size="small" color="warning" variant="outlined" />
                      : targetPrice < currentPrice
                        ? <Chip label={formatPrice(targetPrice - currentPrice, currencySymbol)} size="small" color="success" variant="outlined" />
                        : '—'
                    }
                  </TableCell>
                </TableRow>
                {QUOTA_DISPLAY.map(({ key, label }) => {
                  const currentVal = currentQuotas[key];
                  const newVal = selectedUpgradeDef?.quotas?.[key] ?? PLAN_QUOTAS[selectedUpgradePlan!]?.[key] ?? 0;
                  const diff = newVal - currentVal;
                  return (
                    <TableRow key={key}>
                      <TableCell>{label}</TableCell>
                      <TableCell align="center">{currentVal === 0 ? '—' : currentVal.toLocaleString()}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>{newVal === 0 ? '—' : newVal.toLocaleString()}</TableCell>
                      <TableCell align="center">
                        {diff > 0 ? (
                          <Chip label={`+${diff.toLocaleString()}`} size="small" color="success" variant="outlined" />
                        ) : diff === 0 ? '—' : (
                          <Chip label={diff.toLocaleString()} size="small" color="error" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="outlined" color="secondary" size="large"
            startIcon={upgradeLoading ? <CircularProgress size={20} color="inherit" /> : <ArrowUpward />}
            disabled={upgradeLoading}
            onClick={onUpgrade}
            sx={{
              fontSize: '1rem', textTransform: 'none', whiteSpace: 'nowrap',
              animation: 'upgradeGlow 2s ease-in-out infinite',
              '@keyframes upgradeGlow': {
                '0%, 100%': {
                  boxShadow: `0 0 4px ${alpha(theme.palette.secondary.main, 0.4)}`,
                  borderColor: theme.palette.secondary.main,
                },
                '50%': {
                  boxShadow: `0 0 14px ${alpha(theme.palette.secondary.main, 0.7)}, 0 0 28px ${alpha(theme.palette.secondary.main, 0.3)}`,
                  borderColor: theme.palette.secondary.light,
                },
              },
            }}
          >
            {upgradeLoading
              ? 'Processing...'
              : `Upgrade to ${selectedUpgradeDef.label} (${billingCycle === 'annual' ? 'Annual' : 'Monthly'})`
            }
          </Button>
        </>
      )}
    </>
  );
};
